import { useQuery } from "@tanstack/react-query";
import type { TaskMetadata } from "../../models";

/**
 * Type for a getTaskMetadata repository function.
 * Each section exports its own getTaskMetadata with the same signature.
 */
export type GetTaskMetadataFn = (taskId: number) => Promise<{
  ok: boolean;
  data?: {
    tables: { 0: { rows: TaskMetadata[] } } | [{ rows: TaskMetadata[] }];
  };
  message?: string;
}>;

/**
 * Query hook for fetching task metadata on-demand (used by HistoryPanel popover).
 *
 * Only fires when a taskId is provided (user opened the notes popover).
 *
 * @param taskId - The task/history entry ID to fetch metadata for, or null when popover is closed
 * @param getTaskMetadataFn - The section-specific getTaskMetadata repository function
 */
export const useTaskMetadataQuery = (
  taskId: number | null,
  getTaskMetadataFn: GetTaskMetadataFn,
) => {
  return useQuery({
    queryKey: ["task-metadata", taskId],
    queryFn: async () => {
      if (!taskId) throw new Error("No taskId provided");
      const resp = await getTaskMetadataFn(taskId);
      if (resp.ok) {
        const tables = resp.data?.tables;
        if (!tables) return [] as TaskMetadata[];
        const firstTable = Array.isArray(tables) ? tables[0] : tables[0];
        return (firstTable?.rows ?? []) as TaskMetadata[];
      }
      throw new Error(resp.message ?? "Failed to load task metadata");
    },
    enabled: !!taskId,
  });
};
